// src/helpers/api/application.ts
import { apiFetch } from "../http-client";
import { getCachedData, invalidateCache } from "../apiCache";
import { dedupedFetch, clearDedupedRequest } from "../requestDeduplicator";

const VITE_API_URL = import.meta.env.VITE_API_URL || "";

const createApiError = (message: string, status: number) => {
  const error: any = new Error(message);
  error.status = status;
  return error;
};

export const fetchApplicationsForServer = async (guildId: string, force = false): Promise<any[]> => {
  const cacheKey = `applications:${guildId}`;
  if (force) { invalidateCache(cacheKey); clearDedupedRequest(cacheKey); }
  return dedupedFetch(cacheKey, () => getCachedData(cacheKey, () => fetchApplicationsDirectly(guildId, force)));
};

async function fetchApplicationsDirectly(guildId: string, force = false): Promise<any[]> {
  const apiUrl = `${VITE_API_URL}/api/applications/guild/${guildId}${force ? "?force=true" : ""}`;
  
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
  let data: any;
  try { 
    data = await response.json(); 
  } catch {
    if (!response.ok) throw createApiError(`HTTP Error ${response.status}`, response.status);
    throw new Error("Failed to parse response");
  }
  if (!response.ok) throw createApiError(data?.error || data?.message || "Failed to fetch applications", response.status);
  
  return data;
}

export const getApplicationDetails = async (guildId: string, applicationId: string): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}`, { method: "GET", credentials: "include" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to fetch application details", response.status);
  return data;
};

export const createApplication = async (payload: { guildId: string; name: string; applyMethod: string; language: string; config?: any }): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/new`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to create application", response.status);
  invalidateCache(`applications:${payload.guildId}`);
  return data.application;
};

export const updateApplicationDetails = async (guildId: string, applicationId: string, appData: any): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(appData),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to update application", response.status);
  invalidateCache(`applications:${guildId}`);
  invalidateCache(`app:${guildId}:${applicationId}`); // 🟢 FIX: Invalidate specific app cache
  return data.application;
};

export const deleteApplication = async (guildId: string, applicationId: string): Promise<void> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}`, { method: "DELETE" });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw createApiError(data.error || data.message || "Failed to delete application", response.status);
  }
  invalidateCache(`applications:${guildId}`);
  invalidateCache(`app:${guildId}:${applicationId}`); // 🟢 FIX: Invalidate specific app cache
};

export const sendApplication = async (guildId: string, applicationId: string, channelId: string): Promise<void> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ channelId }),
    allow404: true
  } as any);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw createApiError(data.error || data.message || "Failed to send application", response.status);
  }
};

export const cloneApplication = async (guildId: string, sourceAppId: string, newName: string): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${sourceAppId}/clone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newName }),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to clone", response.status);
  invalidateCache(`applications:${guildId}`);
  return data.application;
};

export const createApplicationShareCode = async (guildId: string, applicationId: string): Promise<{ code: string; expiresAt: string; replacedExisting: boolean }> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}/share`, { method: "POST" });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to create share code", response.status);
  return data;
};

export const inspectApplicationShareCode = async (code: string): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/share/${code}`, { method: "GET" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw createApiError(data.error || data.message || "Invalid share code", response.status);
  return data;
};

export const createApplicationFromShareCode = async (payload: any): Promise<any> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/share/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to apply share code", response.status);
  invalidateCache(`applications:${payload.guildId}`);
  return data.application;
};

export const revertApplicationToFree = async (guildId: string, applicationId: string): Promise<void> => {
  const response = await apiFetch(`${VITE_API_URL}/api/applications/${guildId}/${applicationId}/revert`, { method: "POST", credentials: "include" });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw createApiError(data.error || data.message || "Failed to revert application", response.status);
  }
  invalidateCache(`applications:${guildId}`);
  invalidateCache(`app:${guildId}:${applicationId}`); // 🟢 FIX: Invalidate specific app cache
};

// ... [REST OF YOUR EXPORTED TEMPLATE FUNCTIONS (fetchAppTemplatesByFilters, checkAppTemplateTier, etc.) REMAIN IDENTICAL] ...
export interface AppTemplateForFrontend {
  id: string;
  applicationId?: string;
  name: string;
  description: string;
  isPremium: boolean;
  icon: string;
  category: "free" | "premium";
  config: any;
  connectedConfigs?: any[];
  usageCount?: number;
  isOfficial?: boolean;
  tags?: string[];
  createdAt?: string;
  translations?: Record<string, any>;
}

export async function fetchAppTemplatesByFilters(
  tier: "all" | "free" | "premium",
  isOfficial: boolean,
  queryParams: Record<string, string | number> = {}
): Promise<{ templates: AppTemplateForFrontend[]; hasMore: boolean; total: number; }> {
  const officialSegment = isOfficial ? "official" : "unofficial";
  let url = `${VITE_API_URL}/api/application-templates/${tier}/${officialSegment}`;

  const params = new URLSearchParams({ sortBy: "usageCount", limit: "10" });
  for (const key in queryParams) params.append(key, String(queryParams[key]));
  url += `?${params.toString()}`;

  const response = await apiFetch(url, { method: "GET", credentials: "include" });
  
  let data: any;
  try {
    data = await response.json();
  } catch (err) {
    if (!response.ok) throw createApiError(`HTTP Error ${response.status}`, response.status);
    throw new Error("Failed to parse server response.");
  }

  if (!response.ok) throw createApiError(data?.error || data?.message || "Failed to fetch templates", response.status);

  const templates = data.templates.map((template: any): AppTemplateForFrontend => ({
    id: template._id,
    applicationId: template.applicationId,
    name: template.title,
    description: template.description,
    isPremium: template.mainAppConfig?.isPremium || false,
    icon: "FileText",
    category: template.mainAppConfig?.isPremium ? "premium" : "free",
    config: template.mainAppConfig || {},
    connectedConfigs: template.connectedAppConfigs || [],
    usageCount: template.usageCount,
    isOfficial: template.isOfficial,
    tags: template.tags || [],
    createdAt: template.createdAt,
    translations: template.translations || {},
  }));

  return { templates, hasMore: data.hasMore || false, total: data.total || templates.length };
}

export async function checkAppTemplateTier(guildId: string, applicationId: string): Promise<any> {
  const response = await apiFetch(`${VITE_API_URL}/api/application-templates/check-tier`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceGuildId: guildId, sourceCustomAppId: applicationId }),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to check tier", response.status);
  return data;
}

export async function createAppTemplate(payload: any): Promise<any> {
  const response = await apiFetch(`${VITE_API_URL}/api/application-templates/from-app`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to create template", response.status);
  return data.template;
}

export async function createApplicationFromTemplate(templateId: string, payload: any): Promise<any> {
  const response = await apiFetch(`${VITE_API_URL}/api/application-templates/${templateId}/apply`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw createApiError(data.error || data.message || "Failed to create application", response.status);
  invalidateCache(`applications:${payload.guildId}`);
  return data.application;
}

export async function fetchAppTemplateDetails(templateId: string): Promise<AppTemplateForFrontend> {
  if (!templateId) throw new Error("A template ID is required to fetch details.");
  const url = `${import.meta.env.VITE_API_URL}/api/application-templates/details/${templateId}`;

  const response = await apiFetch(url, { method: "GET", credentials: "include" });
  let template: any;
  try {
    template = await response.json();
  } catch {
    throw new Error(`HTTP Error ${response.status}`);
  }

  if (!response.ok) throw new Error(template?.error || template?.message || "Failed to fetch template");

  return {
    id: template._id,
    applicationId: template.applicationId,
    name: template.title,
    description: template.description,
    isPremium: template.mainAppConfig?.isPremium || false,
    icon: "FileText",
    category: template.mainAppConfig?.isPremium ? "premium" : "free",
    config: template.mainAppConfig,
    connectedConfigs: template.connectedAppConfigs,
    usageCount: template.usageCount,
    isOfficial: template.isOfficial,
    tags: template.tags || [],
    createdAt: template.createdAt,
    translations: template.translations || {},
  };
}

export type AppReportReason = "profanity" | "hate_speech" | "spam_or_misleading" | "malicious_content" | "other";

export const reportAppTemplate = async (templateId: string, reason: AppReportReason, additionalInfo?: string): Promise<{ message: string }> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/application-templates/${templateId}/report`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ reason, additionalInfo }),
  });

  const responseData = await response.json();
  if (!response.ok) throw new Error(responseData.error || responseData.message || "Failed to report template");
  return responseData;
};

export const fetchAdminAppTemplates = async (filters: any, page: number): Promise<any> => {
  const params = new URLSearchParams({
    page: String(page),
    search: filters.search || "",
    status: filters.status || "",
    isOfficial: filters.isOfficial || "",
    sortOrder: filters.sortOrder || "desc",
  });

  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-templates?${params.toString()}`, { method: "GET", credentials: "include" });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch admin app templates");
  }
  return response.json();
};

export const updateAdminAppTemplateStatus = async (templateId: string, action: "approve" | "reject"): Promise<any> => {
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-templates/${templateId}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
    credentials: "include"
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to update status");
  }
  return response.json();
};

export const deleteAdminAppTemplate = async (templateId: string): Promise<any> => {
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-templates/${templateId}`, {
    method: "DELETE",
    credentials: "include"
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to delete template");
  }
  return response.json();
};

export const fetchAdminAppReports = async (page: number, status: string = "open", search: string = ""): Promise<any> => {
  const params = new URLSearchParams({
    page: String(page),
    status,
    search
  });
  
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-reports?${params.toString()}`, { method: "GET", credentials: "include" });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch reports");
  }
  return response.json();
};

export const updateAdminAppReportStatus = async (reportId: string, status: "resolved" | "under_review"): Promise<any> => {
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-reports/${reportId}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
    credentials: "include"
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to update report");
  }
  return response.json();
};

export const updateAdminAppTemplateDetails = async (templateId: string, payload: { title: string; description: string; tags: string[] }): Promise<any> => {
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-templates/${templateId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to update template details");
  }
  return response.json();
};

export async function updateAdminAppTemplateConfig(templateId: string, payload: { mainAppConfig: any; connectedAppConfigs?: any[]; }): Promise<any> {
  const response = await apiFetch(`${import.meta.env.VITE_API_URL}/api/moderation/app-templates/${templateId}/config`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || data.message || "Failed to update config");
  return data;
}

export const updateApplicationMessage = async (
  guildId: string, 
  applicationId: string, 
  messageLink: string
): Promise<any> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/applications/${guildId}/${applicationId}/update`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messageLink }),
    allow404: true
  } as any);
  
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || data.message || "Failed to update application message");
  return data;
};